import { useEffect, useState, type ReactNode } from 'react'
import { AlertTriangle, ArrowLeft, Info, Loader2 } from 'lucide-react'
import {
  createBroadcastTarget,
  esServidorViejo,
  fetchEmisoras,
  fetchGruposDeEmisora,
  updateBroadcastTarget,
  type BroadcastTarget,
  type BroadcastTargetInput,
  type CuentaEmisora,
  type GrupoDeWhatsapp,
  type RespuestaDeHermes,
} from './broadcastApi'

interface DifusionTargetFormProps {
  /** `null` = alta; si viene, se edita ese destino. */
  editing: BroadcastTarget | null
  onCancel: () => void
  onSaved: () => void
}

/** `productos` es una lista; en el formulario se escribe separada por comas. */
const aLista = (texto: string): string[] =>
  texto
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)

const campo =
  'w-full rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-brand'

const etiqueta = 'block text-sm font-semibold text-ink'
const ayuda = 'block text-xs text-ink-subtle'
const aviso = 'flex items-start gap-2 text-xs text-amber-700'

/**
 * Marcador del resumen de la versión. Una plantilla escrita a mano que no lo
 * nombre manda el aviso SIN el resumen —titular, versión y enlace, «en seco»—,
 * y eso no produce ningún error en ninguna parte: el mensaje sale, solo que
 * pelado. Se avisa aquí porque el panel es el único sitio donde alguien puede
 * verlo antes de que salga al grupo.
 */
const MARCADOR_RESUMEN = '{{resumen}}'

/**
 * Lo que se lee en el desplegable de destinos: qué es cada entrada además de
 * su nombre (goal 7). Una comunidad y su canal de anuncios se llaman igual, y
 * el canal es lo que se elige, así que lleva el nombre de la comunidad. Con
 * la clase desconocida —servidor anterior— se enseña el nombre a secas.
 */
const rotuloDeDestino = (g: GrupoDeWhatsapp): string => {
  const nombre = g.nombre || g.id
  const comunidad = g.comunidad?.nombre || g.comunidad?.id
  switch (g.clase) {
    case 'canal_de_anuncios':
      return `Canal de anuncios · ${comunidad || nombre}`
    case 'grupo_de_comunidad':
      return comunidad
        ? `${nombre} · grupo de la comunidad ${comunidad}`
        : `${nombre} · grupo de una comunidad`
    case 'grupo':
      return `${nombre} · grupo`
    default:
      return nombre
  }
}

/**
 * Cómo se está rellenando un campo que tiene lista.
 *
 * `auto` es el estado normal y lo decide la pantalla: hay lista y el valor está
 * en ella ⇒ desplegable; si no ⇒ a mano. `lista` y `mano` son la decisión
 * EXPLÍCITA de quien rellena el formulario, y ganan.
 */
type Modo = 'auto' | 'lista' | 'mano'

/**
 * El sobre que se fabrica cuando la lectura ni siquiera llegó (red caída, 404
 * del servidor que todavía no la publica). El portal nunca devuelve 500 —el
 * fail-open del goal 5 contesta lista vacía con su motivo—, así que este camino
 * cubre solo lo que pasa ANTES de él.
 */
const sobreCaido = (err: unknown, que: string): RespuestaDeHermes<never> =>
  esServidorViejo(err)
    ? {
        disponible: false,
        motivo: 'SERVIDOR_VIEJO',
        detalle: `Este portal todavía no publica ${que}: el servidor se despliega antes que la web. Escríbelo a mano.`,
        items: [],
      }
    : {
        disponible: false,
        motivo: 'SIN_CONEXION',
        detalle: `No se pudieron consultar ${que}. Escríbelo a mano y vuelve a intentarlo más tarde.`,
        items: [],
      }

interface CampoElegibleProps {
  id: string
  rotulo: string
  valor: string
  onChange: (valor: string) => void
  opciones: { valor: string; rotulo: string }[]
  /** Mientras se pide la lista: el valor se enseña, inerte. */
  cargando: boolean
  /** Por qué no hay lista que ofrecer. `null` cuando la hay. */
  motivo: string | null
  aMano: boolean
  onEscribirAMano: () => void
  onElegirDeLaLista: () => void
  placeholderLista: string
  placeholderTexto: string
  children?: ReactNode
}

/**
 * Un campo que se elige de una lista **o** se escribe a mano.
 *
 * 🚨 El texto libre no es un modo degradado que haya que esconder: es la salida
 * de emergencia del formulario. Si Hermes no contesta —o el valor guardado ya
 * no aparece en su lista— se sigue pudiendo dar de alta y editar, que es lo
 * contrario de quedarse sin poder configurar la difusión porque un servicio
 * vecino está caído.
 *
 * El desplegable enseña el **nombre** y guarda el **identificador**; debajo del
 * elegido se ve el identificador en pequeño, que es como aparece un destino en
 * el registro de envíos.
 */
function CampoElegible({
  id,
  rotulo,
  valor,
  onChange,
  opciones,
  cargando,
  motivo,
  aMano,
  onEscribirAMano,
  onElegirDeLaLista,
  placeholderLista,
  placeholderTexto,
  children,
}: CampoElegibleProps) {
  const hayLista = opciones.length > 0

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={id} className={etiqueta}>
          {rotulo}
        </label>
        {/*
          El conmutador solo aparece cuando hay lista: sin ella el texto libre
          no es una elección —es lo único que hay— y ofrecer «elegir de la
          lista» sería ofrecer una lista vacía.
        */}
        {!cargando && hayLista && (
          <button
            type="button"
            onClick={aMano ? onElegirDeLaLista : onEscribirAMano}
            className="text-xs font-semibold text-brand hover:underline"
          >
            {aMano ? 'Elegir de la lista' : 'Escribirlo a mano'}
          </button>
        )}
      </div>

      {cargando ? (
        <>
          <input id={id} value={valor} readOnly disabled className={campo} />
          <span className={ayuda}>
            <Loader2 className="mr-1 inline size-3 animate-spin" />
            Consultando Hermes…
          </span>
        </>
      ) : aMano ? (
        <input
          id={id}
          value={valor}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholderTexto}
          className={campo}
        />
      ) : (
        <>
          <select
            id={id}
            value={valor}
            onChange={(e) => onChange(e.target.value)}
            className={campo}
          >
            <option value="">{placeholderLista}</option>
            {opciones.map((o) => (
              <option key={o.valor} value={o.valor}>
                {o.rotulo}
              </option>
            ))}
          </select>
          {valor && (
            <span className={ayuda}>
              <code>{valor}</code>
            </span>
          )}
        </>
      )}

      {motivo && <span className={ayuda}>{motivo}</span>}
      {children}
    </div>
  )
}

/**
 * Alta y edición de un destino de difusión (goals `difusion-de-novedades 2`
 * y 6).
 *
 * 🚨 El formulario **no valida el destino por su cuenta**: manda lo elegido —o
 * lo escrito— y enseña el mensaje del servidor. El DTO es la única puerta de
 * verdad, también la de quien llame al API sin pasar por aquí, y duplicar la
 * regla en el navegador garantiza que un día divergirán y la pantalla
 * rechazará algo que el servidor acepta, o al revés. Elegir de una lista hace
 * **improbable** el error, no imposible.
 */
export function DifusionTargetForm({
  editing,
  onCancel,
  onSaved,
}: DifusionTargetFormProps) {
  const [nombre, setNombre] = useState(editing?.nombre ?? '')
  const [chatbotId, setChatbotId] = useState(editing?.chatbotId ?? '')
  const [destino, setDestino] = useState(editing?.destino ?? '')
  const [productos, setProductos] = useState(
    (editing?.productos ?? []).join(', '),
  )
  const [plantilla, setPlantilla] = useState(editing?.plantilla ?? '')
  const [activo, setActivo] = useState(editing?.activo ?? true)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [emisoras, setEmisoras] =
    useState<RespuestaDeHermes<CuentaEmisora> | null>(null)
  /**
   * Los grupos van **con la cuenta a la que pertenecen**. Guardar la clave
   * junto al sobre deja «¿esto es de la cuenta que hay elegida ahora?» y «¿está
   * cargando?» como cosas DERIVADAS: sin ella harían falta dos estados más que
   * hay que acordarse de limpiar, y el olvido se paga enseñando los grupos de
   * la cuenta anterior.
   */
  const [gruposDe, setGruposDe] = useState<{
    clave: string
    sobre: RespuestaDeHermes<GrupoDeWhatsapp>
  } | null>(null)
  const [modoCuenta, setModoCuenta] = useState<Modo>('auto')
  const [modoDestino, setModoDestino] = useState<Modo>('auto')

  useEffect(() => {
    let vivo = true
    fetchEmisoras()
      .then((sobre) => vivo && setEmisoras(sobre))
      .catch(
        (err: unknown) =>
          vivo && setEmisoras(sobreCaido(err, 'las cuentas emisoras')),
      )
    return () => {
      vivo = false
    }
  }, [])

  const cargandoCuentas = emisoras === null
  const cuentas = emisoras?.disponible ? emisoras.items : []
  const cuentaElegida =
    cuentas.find((c) => c.chatbotId === chatbotId.trim()) ?? null

  /**
   * A quién se le piden los grupos. **Solo a una cuenta de la lista**: si el
   * identificador se escribió a mano es porque Hermes no lo ofrece —la cuenta
   * de ECOSISTEMA no se ofrece a propósito— o porque no hay a quién preguntar,
   * y disparar por cada tecla sería una petición por pulsación.
   */
  const claveDeGrupos = cuentaElegida?.chatbotId ?? ''

  useEffect(() => {
    if (!claveDeGrupos) return
    let vivo = true
    fetchGruposDeEmisora(claveDeGrupos)
      .then((sobre) => vivo && setGruposDe({ clave: claveDeGrupos, sobre }))
      .catch(
        (err: unknown) =>
          vivo &&
          setGruposDe({
            clave: claveDeGrupos,
            sobre: sobreCaido(err, 'los grupos de esa cuenta'),
          }),
      )
    return () => {
      vivo = false
    }
  }, [claveDeGrupos])

  const grupos = gruposDe?.clave === claveDeGrupos ? gruposDe.sobre : null
  const cargandoGrupos = claveDeGrupos !== '' && grupos === null
  const gruposDeLaCuenta = grupos?.disponible ? grupos.items : []
  /**
   * 🚨 La comunidad padre NO se ofrece (goal 7): no recibe mensajes, y un
   * anuncio mandado ahí no llega a nadie sin que nada avise. Lo que se elige
   * es su canal de anuncios. Con `clase: 'desconocida'` (un servidor anterior)
   * se ofrece todo, como antes: un servidor viejo no puede volver la pantalla
   * inservible ni mentirosa. A mano sigue cabiendo cualquier `@g.us`: el DTO
   * no lo distingue, y es una limitación declarada del goal 6.
   */
  const gruposOfrecidos = gruposDeLaCuenta.filter(
    (g) => g.clase !== 'comunidad',
  )
  const hayComunidades = gruposDeLaCuenta.some((g) => g.clase === 'comunidad')

  const cuentaEnLista = chatbotId.trim() === '' || cuentaElegida !== null
  const grupoEnLista =
    destino.trim() === '' || gruposOfrecidos.some((g) => g.id === destino.trim())

  const cuentaAMano =
    modoCuenta === 'mano' ||
    (modoCuenta === 'auto' &&
      !cargandoCuentas &&
      (cuentas.length === 0 || !cuentaEnLista))
  const destinoAMano =
    modoDestino === 'mano' ||
    (modoDestino === 'auto' &&
      !cargandoGrupos &&
      (gruposOfrecidos.length === 0 || !grupoEnLista))

  /**
   * 🚨 Cambiar de cuenta **vacía el destino**: un `@g.us` pertenece a la cuenta
   * que está dentro de ese grupo, y dárselo a otra es un destino al que no se
   * puede entregar. Vale para las dos formas de cambiarla —el desplegable y el
   * texto libre—, porque por los dos caminos se comete el mismo error.
   */
  const cambiarCuenta = (valor: string) => {
    setChatbotId(valor)
    if (valor.trim() !== chatbotId.trim()) {
      setDestino('')
      setModoDestino('auto')
    }
  }

  const elegirCuentaDeLaLista = () => {
    setModoCuenta('lista')
    // Lo escrito a mano no está en la lista: se suelta a propósito y a la
    // vista, en vez de dejar un desplegable en blanco encima de un valor que
    // sigue vivo y que nadie ve.
    if (!cuentaElegida) cambiarCuenta('')
  }

  const elegirGrupoDeLaLista = () => {
    setModoDestino('lista')
    if (!gruposOfrecidos.some((g) => g.id === destino.trim())) setDestino('')
  }

  const motivoCuentas =
    !cargandoCuentas && cuentas.length === 0
      ? (emisoras?.detalle ??
        'Hermes no ofrece ninguna cuenta emisora. Escribe el identificador a mano.')
      : null

  const motivoGrupos = (() => {
    if (chatbotId.trim() === '')
      return 'Elige antes la cuenta emisora: las comunidades y grupos que se ofrecen son los suyos.'
    if (!claveDeGrupos && !cargandoCuentas)
      return 'La cuenta emisora no sale de la lista de Hermes, así que no hay comunidades ni grupos que ofrecer. Escribe también el destino.'
    if (cargandoGrupos || gruposOfrecidos.length > 0) return null
    return (
      grupos?.detalle ??
      'Esta cuenta no está en ninguna comunidad ni grupo. Escribe el destino a mano.'
    )
  })()

  const completo =
    nombre.trim() !== '' && chatbotId.trim() !== '' && destino.trim() !== ''

  const guardar = async () => {
    setGuardando(true)
    setError(null)
    const cuerpo: BroadcastTargetInput = {
      nombre: nombre.trim(),
      chatbotId: chatbotId.trim(),
      destino: destino.trim(),
      productos: aLista(productos),
      plantilla: plantilla.trim() ? plantilla.trim() : null,
      activo,
    }
    try {
      if (editing) await updateBroadcastTarget(editing.id, cuerpo)
      else await createBroadcastTarget(cuerpo)
      onSaved()
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'No se pudo guardar el destino',
      )
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="grid size-9 place-items-center rounded-lg border border-line text-ink-muted hover:text-ink"
          aria-label="Volver"
        >
          <ArrowLeft className="size-4" />
        </button>
        <h1 className="font-serif text-[28px] font-normal leading-[1.1] tracking-tight text-ink">
          {editing ? 'Editar destino' : 'Nuevo destino'}
        </h1>
      </header>

      {/*
        Las dos frases del goal 2, que siguen valiendo para el modo a mano. La
        primera cambió de CONTENIDO en el goal 6 —ya sí hay listado de grupos—
        pero no de motivo: sigue explicando de dónde sale un `@g.us` cuando hay
        que escribirlo. La segunda no puede irse: el rechazo de una cuenta Kapso
        aparece en el registro de envíos, no aquí.
      */}
      <div className="flex gap-3 rounded-card border border-line bg-panel p-4 text-sm text-ink-muted">
        <Info className="mt-0.5 size-4 shrink-0 text-brand" />
        <div className="space-y-2">
          <p>
            <strong className="font-semibold text-ink">
              De dónde sale el identificador del grupo:
            </strong>{' '}
            normalmente se{' '}
            <strong className="font-semibold text-ink">
              elige de la lista
            </strong>
            , que la trae Hermes. Si Hermes no contesta o el grupo no aparece,
            se mete el número emisor en el grupo de WhatsApp y se lee su
            identificador (<code>…@g.us</code>) del registro de Hermes, para
            escribirlo a mano.
          </p>
          <p>
            <strong className="font-semibold text-ink">
              Para anunciar a una comunidad se elige su canal de anuncios.
            </strong>{' '}
            La comunidad en sí no recibe mensajes, así que no sale en la lista;
            a mano se acepta cualquier <code>…@g.us</code> y el servidor no lo
            distingue, con lo que un anuncio mandado a la comunidad no llega a
            nadie y nada avisa.
          </p>
          <p>
            <strong className="font-semibold text-ink">
              Un canal de WhatsApp también vale como destino, y se pega a mano.
            </strong>{' '}
            Los canales no salen en la lista porque WhatsApp no deja
            preguntarlos. Su identificador (<code>…@newsletter</code>) sale de
            «Ver comunidades, grupos y canales» en la tarjeta de Difusión del
            panel de Hermes, pegando ahí el enlace del canal. La cuenta emisora
            <strong className="font-semibold text-ink">
              {' '}tiene que ser administradora del canal
            </strong>
            : si solo lo sigue, la publicación no sale.
          </p>
          <p>
            <strong className="font-semibold text-ink">
              La cuenta emisora tiene que ser Baileys.
            </strong>{' '}
            Una cuenta Kapso rechaza cualquier destino de grupo y cualquier canal
            de WhatsApp —la API oficial de Meta no tiene ni grupos ni canales—, y
            ese rechazo se ve en el registro de envíos, no aquí.
          </p>
        </div>
      </div>

      <div className="grid gap-4 rounded-card border border-line bg-panel p-5 sm:grid-cols-2">
        {/*
          El texto de ayuda va FUERA del <label>: dentro, pasa a formar parte
          del nombre accesible del campo — el lector de pantalla anunciaría el
          párrafo entero al enfocarlo, y ninguna consulta por etiqueta lo
          encuentra por su nombre.
        */}
        <div className="space-y-1">
          <label htmlFor="difusion-nombre" className={etiqueta}>
            Nombre
          </label>
          <input
            id="difusion-nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Grupo de clientes TECOPOS"
            className={campo}
          />
        </div>

        <CampoElegible
          id="difusion-chatbot"
          rotulo="Cuenta emisora"
          valor={chatbotId}
          onChange={cambiarCuenta}
          opciones={cuentas.map((c) => ({
            valor: c.chatbotId,
            rotulo: c.rotulo || c.chatbotId,
          }))}
          cargando={cargandoCuentas}
          motivo={motivoCuentas}
          aMano={cuentaAMano}
          onEscribirAMano={() => setModoCuenta('mano')}
          onElegirDeLaLista={elegirCuentaDeLaLista}
          placeholderLista="Elige la cuenta que envía…"
          placeholderTexto="Identificador de la cuenta en Hermes (chatbotId)"
        >
          {/*
            Lo que Hermes sabe de la cuenta elegida y este formulario no podía
            saber hasta el goal 5. No bloquea nada —la puerta sigue siendo el
            DTO—: avisa antes de que el rechazo aparezca en el registro.
          */}
          {cuentaElegida && !cuentaElegida.admiteGrupos && (
            <span className={ayuda}>
              Esta cuenta no admite grupos (Kapso): solo se le puede poner un
              teléfono.
            </span>
          )}
          {cuentaElegida?.admiteGrupos && !cuentaElegida.conectada && (
            <span className={ayuda}>
              Esta cuenta no tiene sesión abierta ahora mismo, así que puede que
              Hermes no consiga listar sus grupos.
            </span>
          )}
        </CampoElegible>

        <div className="sm:col-span-2">
          <CampoElegible
            id="difusion-destino"
            rotulo="Destino"
            valor={destino}
            onChange={setDestino}
            opciones={gruposOfrecidos.map((g) => ({
              valor: g.id,
              rotulo: rotuloDeDestino(g),
            }))}
            cargando={cargandoGrupos}
            motivo={motivoGrupos}
            aMano={destinoAMano}
            onEscribirAMano={() => setModoDestino('mano')}
            onElegirDeLaLista={elegirGrupoDeLaLista}
            placeholderLista="Elige la comunidad o el grupo que recibe…"
            placeholderTexto="120363023456789012@g.us, …@newsletter o 5355555555"
          >
            <span className={ayuda}>
              El identificador de la comunidad o del grupo (
              <code>…@g.us</code>), el de un canal de WhatsApp (
              <code>…@newsletter</code>, se pega a mano) o un teléfono en formato
              internacional.
            </span>
            {hayComunidades && (
              <span className={ayuda}>
                Una comunidad se anuncia por su canal de anuncios; la comunidad
                en sí no recibe mensajes, por eso no está en la lista.
              </span>
            )}
          </CampoElegible>
        </div>

        <div className="space-y-1 sm:col-span-2">
          <label htmlFor="difusion-productos" className={etiqueta}>
            Productos
          </label>
          <input
            id="difusion-productos"
            value={productos}
            onChange={(e) => setProductos(e.target.value)}
            placeholder="tecopos-admin, tecopos-pv-web"
            className={campo}
          />
          <span className={ayuda}>
            Repos cuyas notas recibe este destino, separados por comas.{' '}
            <strong className="font-semibold">Vacío = todos.</strong>
          </span>
        </div>

        <div className="space-y-1 sm:col-span-2">
          <label htmlFor="difusion-plantilla" className={etiqueta}>
            Plantilla
          </label>
          <textarea
            id="difusion-plantilla"
            value={plantilla}
            onChange={(e) => setPlantilla(e.target.value)}
            rows={4}
            placeholder="Vacío = el texto por defecto"
            className={`${campo} font-mono`}
          />
          <span className={ayuda}>
            Marcadores: <code>{'{{producto}}'}</code>,{' '}
            <code>{'{{version}}'}</code>, <code>{'{{titulo}}'}</code>,{' '}
            <code>{'{{resumen}}'}</code> y <code>{'{{enlace}}'}</code>. La
            negrita de WhatsApp es{' '}
            <strong className="font-semibold">un</strong> asterisco, y los
            emojis se escriben tal cual.
          </span>
          <span className={ayuda}>
            Escribe una plantilla solo si quieres otro texto:{' '}
            <strong className="font-semibold">
              el de por defecto ya lleva el resumen de la versión
            </strong>{' '}
            y sus emojis. Una plantilla a mano que no nombre{' '}
            <code>{'{{resumen}}'}</code> manda el aviso sin él. Si un marcador se
            queda sin valor, su línea entera desaparece del mensaje.
          </span>
          {plantilla.trim() && !plantilla.includes(MARCADOR_RESUMEN) ? (
            <span className={aviso}>
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <span>
                Esta plantilla no lleva <code>{'{{resumen}}'}</code>: el grupo
                recibirá el aviso sin el resumen de la versión.
              </span>
            </span>
          ) : null}
        </div>

        <div className="flex items-center gap-2 sm:col-span-2">
          <input
            id="difusion-activo"
            type="checkbox"
            checked={activo}
            onChange={(e) => setActivo(e.target.checked)}
            className="size-4 rounded border-line"
          />
          <label htmlFor="difusion-activo" className={etiqueta}>
            Activo (recibe los avisos de versiones nuevas)
          </label>
        </div>
      </div>

      {error && (
        <p role="alert" className="text-sm font-medium text-danger">
          {error}
        </p>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void guardar()}
          disabled={!completo || guardando}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
        >
          {guardando && <Loader2 className="size-4 animate-spin" />}
          {editing ? 'Guardar cambios' : 'Crear destino'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink-muted hover:text-ink"
        >
          Cancelar
        </button>
      </div>
    </div>
  )
}
